import { paths } from '@/routes/paths'

/**
 * The Inventory module's eight sections, one route each.
 *
 * The bar shows three. Stock Items, Recipes, Modifiers, Units and Locations —
 * what stock is and where it is held — sit under Setup; Counts and Adjustments —
 * the two ways stock is corrected by hand — share a Movement tab. Each grouped
 * tab has a toggle between its sections. See `inventoryTabGroups`. Each section
 * keeps its own route, so a link or reload still lands on the one it named.
 *
 * Its own file rather than an export beside the page component, for the reason
 * `products-tabs.ts` gives: a module exporting both a component and a constant
 * opts out of React Fast Refresh, and `app-routes.tsx` needs these names.
 */
export const InventoryTab = {
  Overview: 'overview',
  StockItems: 'stock-items',
  Counts: 'counts',
  Adjustments: 'adjustments',
  Recipes: 'recipes',
  Modifiers: 'modifiers',
  Units: 'units',
  Locations: 'locations',
} as const

export type InventoryTab = (typeof InventoryTab)[keyof typeof InventoryTab]

/** The bar's keys for the tabs that each stand for more than one section. */
export const InventoryTabGroup = {
  Setup: 'setup',
  Movement: 'movement',
} as const

export type InventoryTabGroup = (typeof InventoryTabGroup)[keyof typeof InventoryTabGroup]

/**
 * The sections behind each grouped tab, in the toggle's order. The first is
 * where the tab opens. A group of one shows no toggle.
 */
export const inventoryTabGroups: Record<InventoryTabGroup, readonly InventoryTab[]> = {
  [InventoryTabGroup.Setup]: [
    InventoryTab.StockItems,
    InventoryTab.Recipes,
    InventoryTab.Modifiers,
    InventoryTab.Units,
    InventoryTab.Locations,
  ],
  [InventoryTabGroup.Movement]: [InventoryTab.Counts, InventoryTab.Adjustments],
}

/** The grouped tab a section sits under, or `null` when it is a tab of its own. */
export function groupOf(tab: InventoryTab): InventoryTabGroup | null {
  for (const [group, tabs] of Object.entries(inventoryTabGroups)) {
    if (tabs.includes(tab)) return group as InventoryTabGroup
  }
  return null
}

/**
 * Each tab's route. The URL is what selects the tab — not state the page holds —
 * so a link, a reload or the back button lands on the tab it meant.
 */
export const inventoryTabPaths: Record<InventoryTab, string> = {
  [InventoryTab.Overview]: paths.inventoryOverview,
  [InventoryTab.StockItems]: paths.inventory,
  [InventoryTab.Counts]: paths.inventoryCounts,
  [InventoryTab.Adjustments]: paths.inventoryAdjustments,
  [InventoryTab.Recipes]: paths.inventoryRecipes,
  [InventoryTab.Modifiers]: paths.inventoryModifiers,
  [InventoryTab.Units]: paths.inventoryUnits,
  [InventoryTab.Locations]: paths.inventoryLocations,
}
